import type { AccountCreatePayload } from '@sola/schema/dotcom/auth/account.ts';
import type {
  AuthPayload,
  AuthResponse,
  ConfirmCreateUserPayload,
  CreateUserPayload,
  ForgotPasswordValidatePayload,
  ForgotResponse,
  RecoverUsernamePayload,
  ResendConfirmationEmailPayload,
  ResendConfirmationResponse,
  VerifyResponse,
} from '@sola/schema/dotcom/auth/api.ts';
import type { AnyObject } from '@sola/schema/dotcom/helpers.ts';
import {
  sendCreateAccountEvent,
  sendLoginEvent,
} from '@sola/shared-utils/analytics-events.ts';
import type { RequestOptions } from '@sola/shared-utils/httpClient.ts';
import { httpPostSigned } from '@sola/shared-utils/httpClient.ts';

// The NEXT_PUBLIC_BASE_URL_AUTH env value isn't required in the auth app
const apiPrefix =
  process.env.NEXT_PUBLIC_BASE_URL_AUTH || process.env.BASE_URL_AUTH || '';

const authEndpoints = {
  accounts: `${apiPrefix}/api/accounts`,
  create: `${apiPrefix}/api/create`,
  createValidate: `${apiPrefix}/api/create-validate`,
  logIn: `${apiPrefix}/api/login`,
  logOut: `${apiPrefix}/api/logout`,
  password: `${apiPrefix}/api/password`,
  passwordValidate: `${apiPrefix}/api/password-validate`,
  resendConfirmation: `${apiPrefix}/api/resend-confirmation`,
  signupValidationCodeResend: `${apiPrefix}/api/signups/validation-code/resend`, // TODO Remove this, with not needing resend anymore
  signup: `${apiPrefix}/api/signups/start`,
  username: `${apiPrefix}/api/username`,
  verify: `${apiPrefix}/api/verify`,
  users: `${apiPrefix}/api/users`,
};

const defaultApiOptions: RequestInit = {
  credentials: 'include',
};

const httpPostCredentials = async <T>(
  endpoint: RequestInfo,
  data?: AnyObject,
  options?: RequestOptions,
) => {
  return await httpPostSigned<T>(endpoint, data ?? {}, {
    ...(options && { options }),
    ...defaultApiOptions,
  });
};

/**
 * Authenticates the user, and stores initial auth user object in redis cache, and returns it.
 */
export const authenticateUser = async (
  payload: AuthPayload,
): Promise<AuthResponse> => {
  const authResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.logIn,
    payload,
  );

  if (authResponse?.success) {
    sendLoginEvent();
  }

  return authResponse;
};

/**
 * Checks if user is authenticated, then returns auth user from redis cache.
 */
export const verifyUser = async (): Promise<VerifyResponse> => {
  // TODO: Why is this a POST without any data? Could it not just be a get?
  return await httpPostCredentials(authEndpoints.verify);
};

/**
 * Logs user out in Cognito, deletes redis cache & removes cookies from header.
 */
export const logoutUser = async () => {
  return await httpPostCredentials(authEndpoints.logOut);
};

/**
 * Create the user with either the email, sending the user on the signup scenario, or globalId and postalCode
 * and stores initial auth user object in redis cache, and returns it. // TODO: Do we return AuthUserCacheAttributesWithNoTokens when created with GlobalId to log user in right away?
 */
export const userCreateFromAccount = async (
  payload: CreateUserPayload,
): Promise<AuthResponse> => {
  const authResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.create,
    payload,
  );

  if (authResponse?.success && payload.globalId && payload.postalCode) {
    sendCreateAccountEvent();
  }

  return authResponse;
};

export const userCreate = async (payload: {
  username: string;
  password: string;
  uniqueId: string;
}): Promise<AuthResponse> => {
  const authResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.users,
    payload,
  );

  if (authResponse?.success) {
    sendCreateAccountEvent();
  }

  return authResponse;
};

export const accountCreate = async (
  payload: AccountCreatePayload,
): Promise<AuthResponse> => {
  const authResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.accounts,
    payload,
  );

  if (authResponse?.success) {
    // TODO: Since this is just the begining of this flow should we have a different event?
    sendCreateAccountEvent(true);
  }

  return authResponse;
};

export const signup = async (
  email: string,
  app?: string,
  returnUrl?: string,
): Promise<AuthResponse> => {
  const authResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.signup,
    {
      email,
      app,
      returnUrl,
    },
  );

  if (authResponse?.success) {
    // TODO: Since this is just the begining of this flow should we have a different event?
    sendCreateAccountEvent(true);
  }

  return authResponse;
};

/**
 * Verify the validation code when creating an account with an email address
 */
export const confirmAccountEmail = async (
  payload: ConfirmCreateUserPayload,
): Promise<AuthResponse> => {
  const confirmResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.createValidate,
    payload,
  );

  if (confirmResponse?.success) {
    sendCreateAccountEvent(true);
  }

  return confirmResponse;
};

/**
 * Create the user with either the email, sending the user on the signup scenario, or globalId and postalCode
 * and stores initial auth user object in redis cache, and returns it.
 */
export const resendConfirmationEmail = async (
  payload: ResendConfirmationEmailPayload,
): Promise<ResendConfirmationResponse> => {
  return await httpPostCredentials(authEndpoints.resendConfirmation, payload);
};

export const signupValidationResend = async (payload: {
  uniqueId: string;
}): Promise<AuthResponse> => {
  return await httpPostCredentials(
    authEndpoints.signupValidationCodeResend,
    payload,
  );
};

interface ForgotPasswordPayload {
  email: string;
  username?: string;
  globalId?: string;
  app?: string;
  returnUrl?: string;
}

/**
 * Initiate the password reset process
 */
export const forgotPassword = async (
  payload: ForgotPasswordPayload,
): Promise<ForgotResponse> => {
  return await httpPostCredentials(authEndpoints.password, payload);
};

/**
 * Validate the confirmation code and reset the password
 */
export const forgotPasswordValidate = async (
  payload: ForgotPasswordValidatePayload,
): Promise<ForgotResponse> => {
  return await httpPostCredentials(authEndpoints.passwordValidate, payload);
};

/**
 * Create the user with either the email, sending the user on the signup scenario, or globalId and postalCode
 * and stores initial auth user object in redis cache, and returns it.
 */
export const recoverUsername = async (
  payload: RecoverUsernamePayload,
): Promise<AuthResponse> => {
  return await httpPostCredentials(authEndpoints.username, payload);
};
